import { Close as CloseIcon } from '@mui/icons-material';
import { Box, Input } from '@mui/material';
import { Side } from '@protos/trading';
import darkTheme from '@shared/themes/darkTheme';
import { createRef, useEffect, useState } from 'react';
import { useTradingContext } from '../TradingContext';
import { OtcQuote } from '@protos/v2/otcQuote';

type NumberValidationResult = 'valid' | 'pending' | 'not a number' | 'too many decimals' | 'empty';
const NVRsThatAllowMoreInput: NumberValidationResult[] = ['valid', 'pending', 'empty'];

const validatePriceStringOnInput = (priceString: string, decimals: number): NumberValidationResult => {
  const asNumber = Number(priceString);

  if (priceString.length === 0) return 'empty';

  // If it's not a number, they might just have started typing one in
  if (isNaN(asNumber)) {
    // Minus, shorthand decimals (e.g .123), negative shorthand decimals (e.g -.123)
    if (['-', '.', '-.'].includes(priceString)) {
      return 'pending';
    }
    return 'not a number';
  }

  const parts = priceString.split('.');
  if (parts.length > 1) {
    const numDecimalsEntered = parts[1].length;
    if (numDecimalsEntered > decimals) {
      return 'too many decimals';
    }
  }

  return 'valid';
};

const TradeInputSection = (props: {
  isEditing: boolean;
  isLoadingPrices: boolean;
  onEditStateChange: (state: Side) => void;
  price: string;
  setPrice: (p: number) => void;
  side: Side;
  tradeState: Side;
  hasError: boolean;
  tickSize: number;
  quote: OtcQuote | undefined;
}) => {
  const { isEditing, isLoadingPrices, onEditStateChange, side, setPrice, tradeState, hasError, price } = props;
  const enabled = (tradeState === side || tradeState === Side.UNSPECIFIED) && !hasError;
  const sideColour = side === Side.BUY ? darkTheme.palette.background.buy : darkTheme.palette.background.sell;

  const inputRef = createRef<HTMLInputElement>();
  const decimals = Math.abs(Math.log10(props.tickSize));
  const priceWithTicks = Number(price).toFixed(decimals);

  const [inputValue, setInputValue] = useState<string>(String(priceWithTicks));

  const updatePrice = (inputPrice: string) => {
    const result = validatePriceStringOnInput(inputPrice, decimals);
    if (result === 'valid') {
      setPrice(Number(inputPrice));
    }
  };

  useEffect(() => {
    if (inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }

    updatePrice(priceWithTicks);
    setInputValue(priceWithTicks);
  }, [isEditing, tradeState, isLoadingPrices]);

  if (isLoadingPrices) {
    return <></>;
  }
  if (isEditing && enabled) {
    return (
      <div style={{ display: 'flex', width: '50%' }}>
        <Input
          value={inputValue}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
            const validationResult = validatePriceStringOnInput(e.target.value, decimals);
            if (NVRsThatAllowMoreInput.includes(validationResult)) {
              setInputValue(e.target.value);
            }

            if (validationResult === 'valid') {
              updatePrice(e.target.value);
            }
          }}
          onFocus={e => {
            e.target.select();
          }}
          sx={{ flexGrow: 2 }}
          inputRef={inputRef}
        />
        <CloseIcon
          onClick={() => onEditStateChange(Side.UNSPECIFIED)}
          sx={{ cursor: 'pointer', fontSize: 'small', position: 'relative', top: '.5em' }}
        />
      </div>
    );
  } else {
    return (
      <div
        style={{
          flexGrow: 1,
          cursor: enabled ? 'pointer' : 'not-allowed',
          width: '50%',
          paddingTop: '4px',
          borderBottom: `1px solid ${sideColour}`,
          display: 'flex',
          opacity: enabled ? 1 : 0.3,
        }}
        onClick={() => {
          if (!enabled) return;
          onEditStateChange(side);
        }}
      >
        {Number(price).toFixed(decimals)}
      </div>
    );
  }
};
export interface TradingPanelPriceEntryProps {
  setBuyPrice: (p: number) => void;
  setSellPrice: (p: number) => void;
  tradeState: Side;
  setTradeState: (state: Side) => void;
  hasError: boolean;
  tickSize: number;
}
export const TradingPanelPriceEntry = (props: TradingPanelPriceEntryProps) => {
  const { prices, isLoadingPrices } = useTradingContext();
  const { setBuyPrice, setSellPrice, tradeState, setTradeState, hasError } = props;

  return (
    <Box sx={{ display: 'flex', width: '100%', height: '2em', gap: '1rem', flexDirection: 'row-reverse' }}>
      {/* Sell section */}
      <TradeInputSection
        isLoadingPrices={isLoadingPrices && prices.buy !== undefined}
        isEditing={tradeState === Side.SELL}
        tradeState={tradeState}
        onEditStateChange={(s: Side) => setTradeState(s)}
        // NOTE: THIS IS A LIMIT AND YES IT'S CONFUSING TO HAVE THE BUY HERE
        price={prices.buy?.price || '0'}
        quote={prices.buy}
        setPrice={(v: number) => setSellPrice(v)}
        side={Side.SELL}
        hasError={hasError}
        tickSize={props.tickSize || 2}
      />

      {/* Buy section */}
      <TradeInputSection
        isLoadingPrices={isLoadingPrices && prices.sell !== undefined}
        isEditing={tradeState === Side.BUY}
        tradeState={tradeState}
        onEditStateChange={(s: Side) => setTradeState(s)}
        // NOTE: THIS IS A LIMIT AND YES IT'S CONFUSING TO HAVE THE SELL HERE
        price={prices.sell?.price || '0'}
        quote={prices.sell}
        setPrice={(v: number) => setBuyPrice(v)}
        side={Side.BUY}
        hasError={hasError}
        tickSize={props.tickSize || 2}
      />
    </Box>
  );
};
